import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Tooltip,
  Badge,
  Container,
  Stack,
  Grid,
  Avatar,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { maintenanceService, interventionService, vehicleService, maintenanceSettingsService, interventionSettingsService, externalTechnicianService, api, ExternalTechnician } from '../../services/api';
import { FRENCH_LABELS } from '../../config/labels';
import type { MaintenanceRecord, Intervention, Vehicle, MaintenanceFormData, InterventionFormData } from '../../types';
import { getDropdownMenuProps } from '../../utils/dropdownProps';
import { SYSTEM_MAINTENANCE_TYPES } from '../../constants/maintenanceTypes';
import InterventionSettingsDialog from '../../components/InterventionSettingsDialog';

type MaintenanceFormDataWithExternalTech = MaintenanceFormData & {
  external_technician_id?: number;
};

type StartPromptState = {
  open: boolean;
  kind: 'maintenance' | 'intervention';
  hasTechnician: boolean;
  maintenanceId?: number | string;
  selecting?: boolean;
  selectedTechnicianId?: number;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`maintenance-tabpanel-${index}`}
      aria-labelledby={`maintenance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Maintenance Settings Dialog Component
const MaintenanceSettingsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSettingsUpdated?: () => void;
}> = ({ open, onClose, onSettingsUpdated }) => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [newType, setNewType] = useState({ name: '', name_en: '', description: '', duration_days: 1 });
  const [editingType, setEditingType] = useState<any>(null);

  const normalizeDurationDays = (value: unknown): number => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 1;
    return Math.max(1, Math.round(n));
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await maintenanceSettingsService.getSettings();
      const data = response.data.data;
      setSettings({
        ...data,
        types: (data?.types || []).map((type: any) => ({
          ...type,
          duration_days: normalizeDurationDays(type.duration_days),
        })),
      });
    } catch (error) {
      console.error('Error loading maintenance settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const handleCreateType = async () => {
    try {
      await maintenanceSettingsService.createType({
        ...newType,
        duration_days: normalizeDurationDays(newType.duration_days),
      });
      setNewType({ name: '', name_en: '', description: '', duration_days: 1 });
      await loadSettings();
      onSettingsUpdated?.();
    } catch (error) {
      console.error('Error creating maintenance type:', error);
    }
  };

  const handleUpdateType = async () => {
    try {
      await maintenanceSettingsService.updateType(editingType.id, {
        ...editingType,
        duration_days: normalizeDurationDays(editingType.duration_days),
      });
      setEditingType(null);
      await loadSettings();
      onSettingsUpdated?.();
    } catch (error) {
      console.error('Error updating maintenance type:', error);
    }
  };

  const handleDeleteType = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce type ?')) {
      try {
        await maintenanceSettingsService.deleteType(id);
        await loadSettings();
        onSettingsUpdated?.();
      } catch (error) {
        console.error('Error deleting maintenance type:', error);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Paramètres des types de maintenance</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Types de maintenance</Typography>
            
            {/* Client Types */}
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Vos types de maintenance
            </Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              {settings?.types?.map((type: any) => (
                <Box key={type.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={type.name} />
                  <IconButton size="small" onClick={() => setEditingType(type)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteType(type.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>

            {/* Add New Type */}
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Ajouter un nouveau type</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Nom du type"
                  value={newType.name}
                  onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Nom en anglais (optionnel)"
                  value={newType.name_en}
                  onChange={(e) => setNewType({ ...newType, name_en: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Description (optionnel)"
                  value={newType.description}
                  onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
                <TextField
                  label="Durée (jours)"
                  type="number"
                  value={newType.duration_days}
                  onChange={(e) => setNewType({ ...newType, duration_days: normalizeDurationDays(e.target.value) })}
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                  size="small"
                />
                <Button variant="contained" onClick={handleCreateType} disabled={!newType.name}>
                  Ajouter le type
                </Button>
              </Stack>
            </Card>

            {/* Edit Type Dialog */}
            {editingType && (
              <Card sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Modifier le type</Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Nom du type"
                    value={editingType.name}
                    onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Nom en anglais"
                    value={editingType.name_en}
                    onChange={(e) => setEditingType({ ...editingType, name_en: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Description"
                    value={editingType.description}
                    onChange={(e) => setEditingType({ ...editingType, description: e.target.value })}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                  <TextField
                    label="Durée (jours)"
                    type="number"
                    value={editingType.duration_days}
                    onChange={(e) => setEditingType({ ...editingType, duration_days: normalizeDurationDays(e.target.value) })}
                    inputProps={{ min: 1, step: 1 }}
                    fullWidth
                    size="small"
                  />
                  <Stack direction="row" spacing={1}>
                    <Button onClick={handleUpdateType} variant="contained" size="small">
                      Sauvegarder
                    </Button>
                    <Button onClick={() => setEditingType(null)} size="small">
                      Annuler
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Maintenance: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [technicians, setTechnicians] = useState<ExternalTechnician[]>([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState<any[]>([]);
  const [interventionTypes, setInterventionTypes] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openInterventionDialog, setOpenInterventionDialog] = useState(false);
  const [showMaintenanceSettings, setShowMaintenanceSettings] = useState(false);
  const [showInterventionSettings, setShowInterventionSettings] = useState(false);
  const [startPrompt, setStartPrompt] = useState<StartPromptState>({
    open: false,
    kind: 'maintenance',
    hasTechnician: false,
  });
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(null);
  const [editingIntervention, setEditingIntervention] = useState<Intervention | null>(null);
  const [formData, setFormData] = useState<MaintenanceFormDataWithExternalTech>({
    vehicle_id: 0,
    maintenance_type: 'scheduled' as any,
    description: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [interventionFormData, setInterventionFormData] = useState<InterventionFormData>({
    vehicle_id: 0,
    intervention_type: '',
    description: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all maintenance data
      const [maintenanceRes, interventionsRes, vehiclesRes, statsRes, typesRes, interventionTypesRes, usersRes] = await Promise.all([
        maintenanceService.getAll(),
        interventionService.getAll(),
        vehicleService.getAll(),
        maintenanceService.getStats(),
        maintenanceSettingsService.getSettings(),
        interventionSettingsService.getSettings(),
        externalTechnicianService.getAll({ is_active: true }),
      ]);
      
      setMaintenanceRecords(maintenanceRes.data.data);
      setInterventions(interventionsRes.data.data);
      setVehicles(vehiclesRes.data.data);
      setStats(statsRes.data.data);
      setTechnicians(usersRes.data.data || []);
      
      // Process maintenance types (now only client-specific)
      const settings = typesRes.data.data;
      setMaintenanceTypes(settings.types || []);
      
      // Process intervention types (now only client-specific)
      const interventionSettings = interventionTypesRes.data.data;
      setInterventionTypes(interventionSettings.types || []);
      
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load maintenance data');
    } finally {
      setLoading(false);
    }
  };

  const refreshMaintenanceTypes = async () => {
    try {
      const typesRes = await maintenanceSettingsService.getSettings();
      const settings = typesRes.data.data;
      setMaintenanceTypes(settings.types || []);
    } catch (error) {
      console.error('Error refreshing maintenance types:', error);
    }
  };

  const refreshInterventionTypes = async () => {
    try {
      const interventionTypesRes = await interventionSettingsService.getSettings();
      const interventionSettings = interventionTypesRes.data.data;
      setInterventionTypes(interventionSettings.types || []);
    } catch (error) {
      console.error('Error refreshing intervention types:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingRecord) {
        // Only allow editing manual records (numeric IDs)
        if (typeof editingRecord.id === 'string') {
          setError('Les enregistrements calculés ne peuvent pas être modifiés');
          return;
        }
        await maintenanceService.update(editingRecord.id, formData);
      } else {
        await maintenanceService.create(formData);
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    }
  };

  const submitIntervention = async (overrides?: Partial<InterventionFormData>) => {
    const payload = { ...interventionFormData, ...overrides };
    if (editingIntervention) {
      await interventionService.update(editingIntervention.id, payload);
    } else {
      await interventionService.create(payload);
    }
    handleCloseInterventionDialog();
    loadData();
  };

  const handleInterventionSubmit = async () => {
    try {
      const isStartingIntervention = interventionFormData.status === 'in_progress'
        && editingIntervention?.status !== 'in_progress';
      if (isStartingIntervention) {
        setStartPrompt({
          open: true,
          kind: 'intervention',
          hasTechnician: !!interventionFormData.external_technician_id,
          selecting: false,
          selectedTechnicianId: interventionFormData.external_technician_id,
        });
        return;
      }

      await submitIntervention(undefined);
    } catch (err) {
      setError('Erreur lors de la sauvegarde de l\'intervention');
      console.error(err);
    }
  };

  const handleEdit = (record: MaintenanceRecord) => {
    setEditingRecord(record);
    setFormData({
      vehicle_id: record.vehicle_id,
      maintenance_type: record.maintenance_type,
      description: record.description,
      start_date: format(new Date(record.start_date), 'yyyy-MM-dd'),
      completion_date: record.completion_date ? format(new Date(record.completion_date), 'yyyy-MM-dd') : undefined,
      cost: record.cost,
      service_provider: record.service_provider,
      external_technician_id: record.external_technician_id,
      notes: record.notes,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number | string) => {
    // Don't allow deleting calculated records
    if (typeof id === 'string' || String(id).startsWith('calc_')) {
      setError('Les enregistrements calculés ne peuvent pas être supprimés');
      return;
    }
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette maintenance ?')) {
      try {
        await maintenanceService.delete(id as number);
        loadData();
      } catch (err) {
        setError('Erreur lors de la suppression');
        console.error(err);
      }
    }
  };

  const runMaintenanceStatusUpdate = async (id: number | string, status: string) => {
    // Handle calculated maintenance records differently
    if (typeof id === 'string' || String(id).startsWith('calc_')) {
      // Extract vehicle info from the calculated record
      const record = maintenanceRecords.find(r => r.id === id);
      if (!record || !record.vehicle) {
        setError('Impossible de trouver les informations du véhicule');
        return;
      }

      if (status === 'completed') {
        // Call the complete calculated maintenance endpoint
        await api.post('/maintenance/calculated/complete', {
          vehicle_id: record.vehicle.id,
          maintenance_type: record.maintenance_type,
          description: record.description
        });
      } else {
        // Call the start calculated maintenance endpoint
        await api.post('/maintenance/calculated/start', {
          vehicle_id: record.vehicle.id,
          maintenance_type: record.maintenance_type,
          description: record.description
        });
      }
      
      // Reload data to refresh the display
      loadData();
    } else {
      // Handle manual maintenance records normally
      await maintenanceService.updateStatus(id, status);
      loadData();
    }
  };

  const handleStatusUpdate = async (id: number | string, status: string) => {
    try {
      if (status === 'in_progress') {
        const record = maintenanceRecords.find(r => r.id === id);
        if (record && !(typeof id === 'string' || String(id).startsWith('calc_'))) {
          setStartPrompt({
            open: true,
            kind: 'maintenance',
            hasTechnician: !!record.external_technician_id,
            maintenanceId: id,
            selecting: false,
            selectedTechnicianId: record.external_technician_id,
          });
          return;
        }
      }

      await runMaintenanceStatusUpdate(id, status);
    } catch (err) {
      setError('Erreur lors de la mise à jour du statut');
      console.error(err);
    }
  };

  const closeStartPrompt = () => {
    setStartPrompt((prev) => ({ ...prev, open: false }));
  };

  const handleStartPromptModify = () => {
    setStartPrompt((prev) => ({ ...prev, selecting: true }));
  };

  const handleStartPromptAdd = () => {
    setStartPrompt((prev) => ({ ...prev, selecting: true }));
  };

  const applySelectedTechnicianIfAny = async (): Promise<boolean> => {
    if (!startPrompt.selectedTechnicianId) return true;

    try {
      if (startPrompt.kind === 'maintenance' && startPrompt.maintenanceId !== undefined) {
        await maintenanceService.update(startPrompt.maintenanceId as number, {
          external_technician_id: startPrompt.selectedTechnicianId,
        });
      }

      setStartPrompt((prev) => ({
        ...prev,
        hasTechnician: true,
        selecting: false,
      }));
      if (startPrompt.kind === 'maintenance') {
        await loadData();
      }
      return true;
    } catch (err) {
      setError('Erreur lors de la mise à jour du technicien');
      console.error(err);
      return false;
    }
  };

  const handleStartPromptContinueOrSkip = async () => {
    const techId = startPrompt.selectedTechnicianId;
    try {
      const ok = await applySelectedTechnicianIfAny();
      if (!ok) return;

      if (startPrompt.kind === 'maintenance' && startPrompt.maintenanceId !== undefined) {
        await runMaintenanceStatusUpdate(startPrompt.maintenanceId, 'in_progress');
      } else if (startPrompt.kind === 'intervention') {
        await submitIntervention(techId ? { external_technician_id: techId } : undefined);
      }
    } catch (err) {
      setError('Erreur lors du démarrage');
      console.error(err);
    } finally {
      closeStartPrompt();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
    setFormData({
      vehicle_id: 0,
      maintenance_type: maintenanceTypes.length > 0 ? maintenanceTypes[0].name : 'scheduled' as any,
      description: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
    });
  };

  const handleCloseInterventionDialog = () => {
    setOpenInterventionDialog(false);
    setEditingIntervention(null);
    setInterventionFormData({
      vehicle_id: 0,
      intervention_type: interventionTypes.length > 0 ? interventionTypes[0].name : '',
      description: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
    });
  };

  const handleEditIntervention = (intervention: Intervention) => {
    setEditingIntervention(intervention);
    setInterventionFormData({
      vehicle_id: intervention.vehicle_id,
      intervention_type: intervention.intervention_type,
      description: intervention.description || '',
      start_date: format(new Date(intervention.start_date), 'yyyy-MM-dd'),
      completion_date: intervention.completion_date ? format(new Date(intervention.completion_date), 'yyyy-MM-dd') : undefined,
      cost: intervention.cost,
      service_provider: intervention.service_provider || '',
      external_technician_id: intervention.external_technician_id,
      notes: intervention.notes || '',
      status: intervention.status,
    });
    setOpenInterventionDialog(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      // Manual maintenance statuses
      case 'scheduled': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      // Vehicle operational statuses
      case 'ready': return 'success';
      case 'under_maintenance': return 'warning';
      case 'washing': return 'info';
      case 'out': return 'primary';
      case 'returned': return 'default';
      default: return 'default';
    }
  };

  const getInterventionTypeLabel = (type: string) => {
    return type; // Simply return the type name as it comes from the database
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      // Manual maintenance statuses
      case 'scheduled': return 'Programmée';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminée';
      case 'cancelled': return 'Annulée';
      // Vehicle operational statuses
      case 'ready': return 'Prêt';
      case 'under_maintenance': return 'En maintenance';
      case 'washing': return 'Lavage';
      case 'out': return 'Sorti';
      case 'returned': return 'Retourné';
      default: return status;
    }
  };

  // Use only stored maintenance records (manual + system-generated)
  const allMaintenanceRecords = maintenanceRecords;

  // Filter maintenance records
  const filteredMaintenanceRecords = allMaintenanceRecords.filter(record => {
    const matchesSearch = !searchTerm || 
      record.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.vehicle?.marque.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.vehicle?.modele.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.vehicle?.registration_number.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || record.status === statusFilter;
    const matchesType = !typeFilter || record.maintenance_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setTypeFilter('');
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      pb: 8 // Add bottom padding for fixed navigation
    }}>
      <Box sx={{ px: 1, py: 1, flex: 1 }}>
        {/* Header Section - Mobile Optimized */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Maintenance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredMaintenanceRecords.length} maintenance{filteredMaintenanceRecords.length > 1 ? 's' : ''} trouvée{filteredMaintenanceRecords.length > 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Actualiser">
              <IconButton 
                onClick={loadData} 
                disabled={loading}
                size="small"
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&:disabled': { bgcolor: 'grey.300' }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }} 
              onClose={() => setError(null)}
              action={
                <Button color="inherit" size="small" onClick={loadData}>
                  Réessayer
                </Button>
              }
            >
              {error}
            </Alert>
          )}
      </Box>

      {/* Statistics Cards - Mobile Optimized */}
      {stats && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Total
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {maintenanceRecords.length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                      Calculées + Manuelles
                    </Typography>
                  </Box>
            </CardContent>
          </Card>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      À Venir
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {maintenanceRecords.filter(r => r.status === 'scheduled').length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                      Calculées auto
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      En Retard
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {maintenanceRecords.filter(r => r.status === 'overdue').length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                      Calculées auto
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Complétées
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {maintenanceRecords.filter(record => record.status === 'completed').length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                      Enregistrées
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}


      {/* Tabs - Mobile Optimized */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            '& .MuiTab-root': { 
              textTransform: 'none',
              fontWeight: 'medium',
              minHeight: 36,
              fontSize: '0.8rem',
              px: 1.5,
              minWidth: 'auto'
            }
          }}
        >
          <Tab label="Maintenances" />
          <Tab label="Interventions" />
          <Tab 
            label={(
              <Badge badgeContent={maintenanceRecords.filter(r => r.status === 'scheduled').length} color="warning">
                À venir
              </Badge>
            )} 
          />
          <Tab 
            label={(
              <Badge badgeContent={maintenanceRecords.filter(r => r.status === 'overdue').length} color="error">
                En retard
              </Badge>
            )} 
          />
        </Tabs>
        </Card>

      {/* All Maintenance Records */}
      <TabPanel value={tabValue} index={0}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          height: 'calc(100vh - 400px)',
          minHeight: 300,
          display: 'flex',
          flexDirection: 'column'
        }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Maintenances
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Paramètres">
                    <IconButton 
                      size="small"
                      onClick={() => setShowMaintenanceSettings(true)}
                      sx={{ 
                        bgcolor: 'secondary.main', 
                        color: 'white',
                        '&:hover': { bgcolor: 'secondary.dark' }
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 0.5
                    }}
                  >
                    Ajouter
                  </Button>
                </Stack>
              </Box>
              
              {/* Filters - Mobile Optimized */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ minWidth: 120, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Statut"
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="scheduled">Programmée</MenuItem>
                    <MenuItem value="in_progress">En cours</MenuItem>
                    <MenuItem value="completed">Terminée</MenuItem>
                    <MenuItem value="cancelled">Annulée</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                    MenuProps={getDropdownMenuProps()}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {SYSTEM_MAINTENANCE_TYPES.map((type) => (
                      <MenuItem 
                        key={type.id} 
                        value={type.name}
                        sx={{ 
                          fontWeight: 600,
                          color: 'primary.main',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          pl: 1.5
                        }}
                      >
                        {type.name}
                      </MenuItem>
                    ))}
                    {maintenanceTypes.length > 0 && (
                      <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem' }}>
                        ──────────────────
                      </MenuItem>
                    )}
                    {maintenanceTypes.map((type) => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name} {type.duration_days && `(${type.duration_days} jours)`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MoreVertIcon />}
                  onClick={resetFilters}
                >
                  Réinitialiser
                </Button>
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {filteredMaintenanceRecords.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <BuildIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucune maintenance trouvée
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm || statusFilter || typeFilter 
                      ? 'Aucun résultat pour les filtres sélectionnés'
                      : 'Commencez par ajouter des maintenances manuelles. Les maintenances calculées apparaissent dans les onglets "À venir" et "En retard".'
                    }
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {filteredMaintenanceRecords.map((record) => (
                    <Card 
                      key={record.id}
                      sx={{ 
                        p: 2,
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { 
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      {/* Header with vehicle info and status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {record.vehicle ? `${record.vehicle.marque} ${record.vehicle.modele}` : 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {record.vehicle?.registration_number}
                          </Typography>
                          <Chip 
                            size="small" 
                            label={record.maintenance_type} 
                            sx={{ fontWeight: 'medium', mr: 1 }}
                          />
                          <Chip 
                            size="small" 
                            color={getStatusColor(record.status) as any}
                            label={getStatusLabel(record.status)}
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(record.start_date), 'dd/MM/yyyy')}
                        </Typography>
                      </Box>

                      {/* Description */}
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        {record.description}
                      </Typography>

                      {/* Cost and actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium" color="text.secondary">
                          {record.cost ? `${record.cost}€` : 'N/A'}
                        </Typography>
                        
                        {/* Action buttons */}
                        {record.is_system_generated ? (
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleStatusUpdate(record.id, 'in_progress')}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                minWidth: 'auto',
                                px: 2,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                                },
                              }}
                            >
                              Start
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleStatusUpdate(record.id, 'completed')}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                minWidth: 'auto',
                                px: 2,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                                },
                              }}
                            >
                              Done
                            </Button>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Voir les détails">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(record)}
                                sx={{ 
                                  color: 'primary.main',
                                  '&:hover': { bgcolor: 'primary.50' }
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!(typeof record.id === 'string' || String(record.id).startsWith('calc_')) && (
                              <Tooltip title="Modifier">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(record)}
                                  sx={{ 
                                    color: 'warning.main',
                                    '&:hover': { bgcolor: 'warning.50' }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {record.status === 'scheduled' && (
                              <Tooltip title="Marquer en cours">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleStatusUpdate(record.id, 'in_progress')}
                                  sx={{ 
                                    color: 'info.main',
                                    '&:hover': { bgcolor: 'info.50' }
                                  }}
                                >
                                  <ScheduleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {record.status === 'in_progress' && (
                              <Tooltip title="Marquer terminée">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleStatusUpdate(record.id, 'completed')}
                                  sx={{ 
                                    color: 'success.main',
                                    '&:hover': { bgcolor: 'success.50' }
                                  }}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!(typeof record.id === 'string' || String(record.id).startsWith('calc_')) && (
                              <Tooltip title="Supprimer">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(record.id)}
                                  sx={{ 
                                    color: 'error.main',
                                    '&:hover': { bgcolor: 'error.50' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </Card>
      </TabPanel>

      {/* Interventions */}
      <TabPanel value={tabValue} index={1}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          height: 'calc(100vh - 400px)',
          minHeight: 300,
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Interventions
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Paramètres">
                    <IconButton 
                      size="small"
                      onClick={() => setShowInterventionSettings(true)}
                      sx={{ 
                        bgcolor: 'secondary.main', 
                        color: 'white',
                        '&:hover': { bgcolor: 'secondary.dark' }
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenInterventionDialog(true)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 0.5
                    }}
                  >
                    Ajouter
                  </Button>
                </Stack>
              </Box>
              
              {/* Filters - Mobile Optimized */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ minWidth: 120, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Statut"
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="scheduled">Programmé</MenuItem>
                    <MenuItem value="in_progress">En cours</MenuItem>
                    <MenuItem value="completed">Terminé</MenuItem>
                    <MenuItem value="cancelled">Annulé</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          zIndex: 9999,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {interventionTypes.map(type => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {interventions
                .filter(intervention => {
                  const matchesSearch = !searchTerm || 
                    intervention.vehicle?.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    intervention.description?.toLowerCase().includes(searchTerm.toLowerCase());
                  const matchesStatus = !statusFilter || intervention.status === statusFilter;
                  const matchesType = !typeFilter || intervention.intervention_type === typeFilter;
                  return matchesSearch && matchesStatus && matchesType;
                })
                .length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <AssignmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucune intervention trouvée
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Commencez par ajouter des interventions
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {interventions
                    .filter(intervention => {
                      const matchesSearch = !searchTerm || 
                        intervention.vehicle?.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        intervention.description?.toLowerCase().includes(searchTerm.toLowerCase());
                      const matchesStatus = !statusFilter || intervention.status === statusFilter;
                      const matchesType = !typeFilter || intervention.intervention_type === typeFilter;
                      return matchesSearch && matchesStatus && matchesType;
                    })
                    .map((intervention) => (
                    <Card 
                      key={intervention.id}
                      sx={{ 
                        p: 2,
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { 
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      {/* Header with vehicle info and status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                            {intervention.vehicle?.registration_number?.charAt(0)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {intervention.vehicle?.registration_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {intervention.vehicle?.marque} {intervention.vehicle?.modele}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={getInterventionTypeLabel(intervention.intervention_type)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>

                      {/* Description */}
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        {intervention.description || 'Aucune description'}
                      </Typography>

                      {/* Date, status, cost and actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(intervention.start_date), 'dd/MM/yyyy')}
                          </Typography>
                          <Chip
                            label={getStatusLabel(intervention.status)}
                            size="small"
                            color={getStatusColor(intervention.status) as any}
                            variant="filled"
                          />
                          <Typography variant="body2" fontWeight="medium" color="text.secondary">
                            {intervention.cost ? `${intervention.cost}€` : '-'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              onClick={() => handleEditIntervention(intervention)}
                              sx={{ 
                                color: 'primary.main',
                                '&:hover': { bgcolor: 'primary.50' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (window.confirm('Êtes-vous sûr de vouloir supprimer cette intervention ?')) {
                                  interventionService.delete(intervention.id).then(() => loadData());
                                }
                              }}
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { bgcolor: 'error.50' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Upcoming Maintenance - Mobile Optimized */}
      <TabPanel value={tabValue} index={2}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            height: 'calc(100vh - 400px)',
            minHeight: 300,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {maintenanceRecords.filter(r => r.status === 'scheduled').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ScheduleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucune maintenance à venir
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toutes les maintenances programmées sont à jour
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {maintenanceRecords.filter(r => r.status === 'scheduled').map((record) => (
                    <Card 
                      key={record.id}
                      sx={{ 
                        p: 2,
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { 
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      {/* Header with vehicle info and type */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {record.vehicle ? `${record.vehicle.marque} ${record.vehicle.modele}` : 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {record.vehicle?.registration_number}
                          </Typography>
                          <Chip 
                            size="small" 
                            label={record.maintenance_type} 
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                          <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                          <Typography variant="body2" fontWeight="medium">
                            {format(new Date(record.start_date), 'dd/MM/yyyy')}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Description */}
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        {record.description}
                      </Typography>

                      {/* Technician and actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {record.technician?.name && (
                          <Typography variant="body2" color="text.secondary">
                            Technicien: {record.technician.name}
                          </Typography>
                        )}
                        
                        {String(record.id).startsWith('calc_') ? (
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleStatusUpdate(record.id, 'in_progress')}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                minWidth: 'auto',
                                px: 2,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                                },
                              }}
                            >
                              Start
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleStatusUpdate(record.id, 'completed')}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                minWidth: 'auto',
                                px: 2,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                                },
                              }}
                            >
                              Done
                            </Button>
                          </Stack>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleStatusUpdate(record.id, 'in_progress')}
                            sx={{
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                              },
                            }}
                          >
                            Commencer
                          </Button>
                        )}
                      </Box>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </Card>
      </TabPanel>

      {/* Overdue Maintenance - Mobile Optimized */}
      <TabPanel value={tabValue} index={3}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2, 
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: 20
              }
            }}
          >
            <Typography variant="body2" fontWeight="medium">
              Attention requise
            </Typography>
            <Typography variant="caption">
              Maintenances en retard - attention urgente
            </Typography>
        </Alert>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            height: 'calc(100vh - 400px)',
            minHeight: 300,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {maintenanceRecords.filter(r => r.status === 'overdue').length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucune maintenance en retard
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toutes les maintenances sont à jour
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {maintenanceRecords.filter(r => r.status === 'overdue').map((record) => {
                    const overdueDays = Math.floor(
                      (new Date().getTime() - new Date(record.start_date).getTime()) / (1000 * 60 * 60 * 24)
                    );
                    return (
                      <Card 
                        key={record.id}
                        sx={{ 
                          p: 2,
                          borderRadius: 2,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          border: '2px solid',
                          borderColor: 'error.main',
                          '&:hover': { 
                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        {/* Header with vehicle info and overdue warning */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {record.vehicle ? `${record.vehicle.marque} ${record.vehicle.modele}` : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {record.vehicle?.registration_number}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                size="small" 
                                label={record.maintenance_type} 
                                sx={{ fontWeight: 'medium' }}
                              />
                              <Chip 
                                size="small" 
                                color="error" 
                                label={`${overdueDays} jours de retard`} 
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                            <WarningIcon sx={{ mr: 1, fontSize: 16 }} />
                            <Typography variant="body2" fontWeight="medium">
                              {format(new Date(record.start_date), 'dd/MM/yyyy')}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Description */}
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                          {record.description}
                        </Typography>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleStatusUpdate(record.id, 'in_progress')}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                minWidth: 'auto',
                                px: 2,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                                },
                              }}
                            >
                              Start
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleStatusUpdate(record.id, 'completed')}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                minWidth: 'auto',
                                px: 2,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                                },
                              }}
                            >
                              Done
                            </Button>
                          </Stack>
                        </Box>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Card>
      </TabPanel>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRecord ? 'Modifier la maintenance' : 'Nouvelle maintenance'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <FormControl fullWidth>
                <InputLabel>Véhicule</InputLabel>
                <Select
                  value={formData.vehicle_id}
                  onChange={(e) => setFormData({ ...formData, vehicle_id: Number(e.target.value) })}
                >
                  {vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.marque} {vehicle.modele} ({vehicle.registration_number})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Type de maintenance</InputLabel>
                <Select
                  value={formData.maintenance_type}
                  onChange={(e) => {
                    setFormData({ ...formData, maintenance_type: e.target.value as any });
                    
                    // Auto-calculate completion date based on maintenance type duration
                    if (e.target.value && formData.start_date) {
                      const selectedType = maintenanceTypes.find(type => type.name === e.target.value);
                      if (selectedType && selectedType.duration_days) {
                        const startDate = new Date(formData.start_date);
                        const completionDate = new Date(startDate);
                        completionDate.setDate(completionDate.getDate() + selectedType.duration_days);
                        setFormData(prev => ({ 
                          ...prev, 
                          maintenance_type: e.target.value as any,
                          completion_date: completionDate.toISOString().split('T')[0]
                        }));
                      }
                    }
                  }}
                  MenuProps={getDropdownMenuProps()}
                >
                  {maintenanceTypes.map((type) => (
                    <MenuItem key={type.id} value={type.name}>
                      {type.name} {type.duration_days && `(${type.duration_days} jours)`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Date de début"
                type="date"
                value={formData.start_date}
                onChange={(e) => {
                  setFormData({ ...formData, start_date: e.target.value });
                  
                  // Auto-calculate completion date if maintenance type is selected
                  if (e.target.value && formData.maintenance_type) {
                    const selectedType = maintenanceTypes.find(type => type.name === formData.maintenance_type);
                    if (selectedType && selectedType.duration_days) {
                      const startDate = new Date(e.target.value);
                      const completionDate = new Date(startDate);
                      completionDate.setDate(completionDate.getDate() + selectedType.duration_days);
                      setFormData(prev => ({ 
                        ...prev, 
                        start_date: e.target.value,
                        completion_date: completionDate.toISOString().split('T')[0]
                      }));
                    }
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Date de fin"
                type="date"
                value={formData.completion_date || ''}
                onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Coût (€)"
                type="number"
                value={formData.cost || ''}
                onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })}
              />
              <TextField
                fullWidth
                label="Prestataire"
                value={formData.service_provider || ''}
                onChange={(e) => setFormData({ ...formData, service_provider: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Technicien</InputLabel>
                <Select
                  value={formData.external_technician_id ?? ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    external_technician_id: (typeof e.target.value === 'string' && e.target.value === '') ? undefined : Number(e.target.value),
                  })}
                  label="Technicien"
                  MenuProps={getDropdownMenuProps()}
                >
                  <MenuItem value="">— Non assigné —</MenuItem>
                  {technicians.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRecord ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Intervention Dialog */}
      <Dialog open={openInterventionDialog} onClose={handleCloseInterventionDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingIntervention ? 'Modifier l\'intervention' : 'Nouvelle intervention'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Véhicule</InputLabel>
              <Select
                value={interventionFormData.vehicle_id}
                onChange={(e) => setInterventionFormData({...interventionFormData, vehicle_id: Number(e.target.value)})}
                label="Véhicule"
              >
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration_number} - {vehicle.marque} {vehicle.modele}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Type d'intervention</InputLabel>
              <Select
                value={interventionFormData.intervention_type}
                onChange={(e) => setInterventionFormData({...interventionFormData, intervention_type: e.target.value as any})}
                label="Type d'intervention"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 9999,
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                {interventionTypes.map(type => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              multiline
              rows={3}
              value={interventionFormData.description}
              onChange={(e) => setInterventionFormData({...interventionFormData, description: e.target.value})}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Date de début"
                type="date"
                value={interventionFormData.start_date}
                onChange={(e) => setInterventionFormData({...interventionFormData, start_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Date de fin"
                type="date"
                value={interventionFormData.completion_date || ''}
                onChange={(e) => setInterventionFormData({...interventionFormData, completion_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Coût"
                type="number"
                value={interventionFormData.cost || ''}
                onChange={(e) => setInterventionFormData({...interventionFormData, cost: Number(e.target.value)})}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Prestataire"
                value={interventionFormData.service_provider || ''}
                onChange={(e) => setInterventionFormData({...interventionFormData, service_provider: e.target.value})}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Technicien</InputLabel>
                <Select
                  value={interventionFormData.external_technician_id ?? ''}
                  onChange={(e) => setInterventionFormData({
                    ...interventionFormData,
                    external_technician_id: (typeof e.target.value === 'string' && e.target.value === '') ? undefined : Number(e.target.value),
                  })}
                  label="Technicien"
                  MenuProps={getDropdownMenuProps()}
                >
                  <MenuItem value="">— Non assigné —</MenuItem>
                  {technicians.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Notes"
              multiline
              rows={2}
              value={interventionFormData.notes || ''}
              onChange={(e) => setInterventionFormData({...interventionFormData, notes: e.target.value})}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInterventionDialog}>Annuler</Button>
          <Button onClick={handleInterventionSubmit} variant="contained">
            {editingIntervention ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={startPrompt.open} onClose={closeStartPrompt} maxWidth="xs" fullWidth>
        <DialogTitle>
          {startPrompt.kind === 'maintenance' ? 'Démarrer la maintenance' : "Démarrer l'intervention"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {startPrompt.hasTechnician
              ? 'Un technicien est déjà assigné. Vous pouvez le modifier ou continuer.'
              : 'Aucun technicien assigné. Vous pouvez en ajouter un maintenant, ou continuer sans assignation.'}
          </Typography>
          {startPrompt.selecting && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Technicien</InputLabel>
              <Select
                label="Technicien"
                value={startPrompt.selectedTechnicianId ?? ''}
                onChange={(e) =>
                  setStartPrompt((prev) => ({
                    ...prev,
                    selectedTechnicianId: typeof e.target.value === 'string' && e.target.value === ''
                      ? undefined
                      : Number(e.target.value),
                  }))
                }
                MenuProps={getDropdownMenuProps()}
              >
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.name}{tech.company ? ` - ${tech.company}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStartPrompt}>Annuler</Button>
          {startPrompt.hasTechnician ? (
            <>
              <Button onClick={handleStartPromptModify} color="inherit">Modifier</Button>
              <Button onClick={handleStartPromptContinueOrSkip} variant="contained">
                Continuer
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleStartPromptAdd} color="inherit">Ajouter</Button>
              <Button onClick={handleStartPromptContinueOrSkip} variant="contained">
                {startPrompt.selectedTechnicianId ? 'Valider' : 'Skip'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      </Box>

      <MaintenanceSettingsDialog
        open={showMaintenanceSettings}
        onClose={() => setShowMaintenanceSettings(false)}
        onSettingsUpdated={refreshMaintenanceTypes}
      />
      
      <InterventionSettingsDialog
        open={showInterventionSettings}
        onClose={() => setShowInterventionSettings(false)}
        onSettingsUpdated={refreshInterventionTypes}
      />
    </Box>
  );
};

export default Maintenance;
